<template>
  <!-- 浮动按钮 -->
  <div
    id="cta-chatwithus"
    v-if="!isOpen"
    class="chat-floating-button"
    @click="handleOpenChat"
  >
    <svg class="chat-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z" fill="white"/>
    </svg>
    <span class="button-text">{{ chatTexts.floatingButtonText }}</span>
  </div>

  <!-- 聊天弹窗 -->
  <Transition name="chat-slide">
    <div v-if="isOpen" class="chat-widget" ref="chatWidgetRef">
      <!-- 头部 -->
      <div class="chat-header">
        <div class="header-left">
          <div class="header-avatar">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="white"/>
            </svg>
          </div>
          <span class="header-title">{{ chatTexts.chatTitle }}</span>
        </div>
        <div class="header-actions">
          <!-- 最小化按钮 - 使用减号图标 -->
          <button class="header-btn minimize-btn" @click="handleMinimize" :title="chatTexts.minimizeChat">
            <span class="btn-icon">−</span>
          </button>
          <!-- 关闭按钮 - 使用 X 图标 -->
          <button class="header-btn close-btn" @click="handleClose" :title="chatTexts.closeChat">
            <span class="btn-icon">×</span>
          </button>
        </div>
      </div>

      <!-- 消息区域 -->
      <div class="chat-messages" ref="messagesContainer">
        <TransitionGroup name="message-fade">
          <div
            v-for="message in displayMessages"
            :key="message.id"
            :class="['message', `message-${message.type}`]"
          >
            <!-- 系统消息 -->
            <div v-if="message.type === 'system'" class="message-bubble system-bubble">
              <div class="message-content" v-html="formatMessage(message.content)"></div>
            </div>

            <!-- 用户消息 -->
            <div v-else-if="message.type === 'user'" class="message-bubble user-bubble">
              <div class="message-content">{{ message.content }}</div>
            </div>

            <!-- 选项按钮 -->
            <div v-else-if="message.type === 'options'" class="options-container">
              <button
                v-for="option in message.options"
                :key="option.key"
                class="option-button"
                @click="handleOptionClick(option.key)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </TransitionGroup>

        <!-- 输入区域 -->
        <div v-if="showInput" class="input-section">
          <input
            v-if="currentStep === 'input_contact'"
            v-model="inputValue"
            :placeholder="getInputPlaceholder"
            class="chat-input"
            @keyup.enter="handleInputSubmit"
          />
          <textarea
            v-else-if="currentStep === 'optional_message'"
            v-model="inputValue"
            :placeholder="chatTexts.optionalMessagePlaceholder"
            class="chat-textarea"
            rows="3"
          ></textarea>
          <div v-if="showInput" class="input-actions">
            <button
              v-if="currentStep === 'optional_message'"
              class="skip-button"
              @click="handleSkip"
            >
              {{ chatTexts.skipButton }}
            </button>
            <button class="send-button" @click="handleInputSubmit">
              {{ chatTexts.sendButton }}
            </button>
          </div>
          <div v-if="validationError" class="validation-error">
            {{ validationError }}
          </div>
        </div>
      </div>

      <!-- 底部 Powered by -->
      <div class="chat-footer">
        <span>Powered by Creston Group</span>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChatWidget, type ServiceType, type ContactMethod } from '../utils/chatWidget';
import { API_BASE_URL } from '../config/config';
import { getAnonymousId } from '../utils/anonymous';
import { saveCTAInfo, CTA_CONFIG } from '../utils/ctaTracking';
import { pushToDataLayer } from '../utils/gtm';
import { getChatLeadParams, getChatOpenParams, getChatEngagedParams } from '../utils/leadTracking';

const { locale, messages } = useI18n();

const {
  isOpen,
  currentStep,
  openChat,
  closeChat,
  minimizeChat,
  selectService,
  selectContactMethod,
  submitContactValue,
  submitMessage,
  skipMessage,
  addMessage,
  _currentStep,
  _messages,
  _leadData,
} = useChatWidget();

// 本地状态
const messagesContainer = ref<HTMLElement | null>(null);
const chatWidgetRef = ref<HTMLElement | null>(null);
const inputValue = ref('');
const validationError = ref('');
const displayMessages = ref<any[]>([]);

// 防止滚动穿透到底层页面 - 阻止所有非聊天窗口的滚动事件
const preventGlobalScroll = (event: WheelEvent | TouchEvent) => {
  // 检查事件目标是否在聊天窗口内
  const chatWidget = chatWidgetRef.value;
  if (chatWidget && chatWidget.contains(event.target as Node)) {
    // 在聊天窗口内，允许滚动
    return;
  }
  // 不在聊天窗口内，阻止滚动
  event.preventDefault();
};

// 阻止背景页面滚动
const lockBodyScroll = () => {
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  // 添加全局滚动事件监听器
  window.addEventListener('wheel', preventGlobalScroll, { passive: false });
  window.addEventListener('touchmove', preventGlobalScroll, { passive: false });
};

const unlockBodyScroll = () => {
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  // 移除全局滚动事件监听器
  window.removeEventListener('wheel', preventGlobalScroll);
  window.removeEventListener('touchmove', preventGlobalScroll);
};

// 监听聊天窗口开关状态
watch(isOpen, (newVal) => {
  if (newVal) {
    lockBodyScroll();
  } else {
    unlockBodyScroll();
  }
});

// 组件卸载时恢复滚动
onBeforeUnmount(() => {
  unlockBodyScroll();
});

// 获取聊天文案
const chatTexts = computed(() => {
  return (messages.value[locale.value] as any)?.chatWidget || {
    floatingButtonText: 'Chat with us',
    chatTitle: 'Creston Support',
    closeChat: 'Close',
    minimizeChat: 'Minimize',
  };
});

// 是否显示输入框
const showInput = computed(() => {
  return currentStep.value === 'input_contact' || currentStep.value === 'optional_message';
});

// 获取输入提示文本
const getInputPlaceholder = computed(() => {
  const method = _leadData.value.contactMethod;
  if (method && chatTexts.value.inputPlaceholder) {
    return chatTexts.value.inputPlaceholder[method] || '';
  }
  return '';
});

// 格式化消息（换行符转 <br>）
const formatMessage = (content: string) => {
  return content.replace(/\n/g, '<br>');
};

// 滚动到底部
const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

// 添加延迟显示的消息
const addDelayedMessage = async (type: 'system' | 'user' | 'options', content: string, options?: any[], delay = 500) => {
  await new Promise(resolve => setTimeout(resolve, delay));
  displayMessages.value.push({
    id: Date.now() + Math.random(),
    type,
    content,
    options,
  });
  await scrollToBottom();
};

// 开始欢迎流程
const startGreetingFlow = async () => {
  displayMessages.value = [];

  // 欢迎语1
  await addDelayedMessage('system', chatTexts.value.greeting1, undefined, 300);

  // 欢迎语2
  await addDelayedMessage('system', chatTexts.value.greeting2, undefined, 800);

  // 欢迎语3
  await addDelayedMessage('system', chatTexts.value.greeting3, undefined, 800);

  // 选择服务问题
  await addDelayedMessage('system', chatTexts.value.chooseServiceTitle, undefined, 600);

  // 服务选项
  const serviceOptions = [
    { key: 'sourcing', label: chatTexts.value.services.sourcing },
    { key: 'maintenance', label: chatTexts.value.services.maintenance },
    { key: 'custom', label: chatTexts.value.services.custom },
    { key: 'exploring', label: chatTexts.value.services.exploring },
  ];
  await addDelayedMessage('options', '', serviceOptions, 400);

  _currentStep.value = 'choose_service';
};

// 处理打开聊天
const handleOpenChat = () => {
  saveCTAInfo(CTA_CONFIG.CHAT_WIDGET);
  openChat('floating_button');

  // 触发 chat_open 事件（意图弱信号，不包含双归因参数）
  const chatOpenParams = getChatOpenParams('creston_chat');
  pushToDataLayer('chat_open', {
    ...chatOpenParams,
    source: 'floating_button',
  });
};

// 处理最小化
const handleMinimize = () => {
  minimizeChat();
};

// 处理关闭
const handleClose = () => {
  closeChat();
};

// 处理选项点击
const handleOptionClick = async (key: string) => {
  // 根据当前步骤处理
  if (currentStep.value === 'choose_service') {
    // 用户选择了服务
    const service = key as ServiceType;
    selectService(service);

    // 触发 chat_engaged 事件（用户第一次交互，包含双归因参数）
    const engagedParams = getChatEngagedParams('creston_chat');
    pushToDataLayer('chat_engaged', {
      ...engagedParams,
      service_type: service,
    });

    // 添加用户选择的消息
    const label = chatTexts.value.services[service];
    await addDelayedMessage('user', label, undefined, 100);

    // 添加确认消息
    const ackMessage = chatTexts.value.acknowledge[service];
    await addDelayedMessage('system', ackMessage, undefined, 600);

    // 询问联系方式
    await addDelayedMessage('system', chatTexts.value.contactWayTitle, undefined, 600);

    // 联系方式选项
    const contactOptions = [
      { key: 'email', label: chatTexts.value.contactMethods.email },
      { key: 'whatsapp/phone', label: chatTexts.value.contactMethods['whatsapp/phone'] },
      { key: 'wechat', label: chatTexts.value.contactMethods.wechat },
    ];
    await addDelayedMessage('options', '', contactOptions, 400);

    _currentStep.value = 'choose_contact_way';
  } else if (currentStep.value === 'choose_contact_way') {
    // 用户选择了联系方式
    const method = key as ContactMethod;
    selectContactMethod(method);

    // 添加用户选择的消息
    const label = chatTexts.value.contactMethods[method];
    await addDelayedMessage('user', label, undefined, 100);

    // 添加输入提示
    const inputPrompt = chatTexts.value.inputContact[method];
    await addDelayedMessage('system', inputPrompt, undefined, 600);

    _currentStep.value = 'input_contact';
    inputValue.value = '';
  }
};

// 验证输入
const validateInput = (): boolean => {
  validationError.value = '';
  const value = inputValue.value.trim();

  if (currentStep.value === 'input_contact') {
    if (!value) {
      validationError.value = chatTexts.value.validation?.required || 'This field is required';
      return false;
    }

    const method = _leadData.value.contactMethod;
    if (method === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        validationError.value = chatTexts.value.validation?.emailInvalid || 'Please enter a valid email address';
        return false;
      }
    }
  }

  return true;
};

// 处理输入提交
const handleInputSubmit = async () => {
  if (!validateInput()) return;

  const value = inputValue.value.trim();

  if (currentStep.value === 'input_contact') {
    submitContactValue(value);

    // 添加用户输入的消息
    await addDelayedMessage('user', value, undefined, 100);

    // 添加可选留言提示
    await addDelayedMessage('system', chatTexts.value.optionalMessagePrompt, undefined, 600);

    inputValue.value = '';
    _currentStep.value = 'optional_message';
  } else if (currentStep.value === 'optional_message') {
    if (value) {
      submitMessage(value);
      await addDelayedMessage('user', value, undefined, 100);
    } else {
      skipMessage();
    }

    // 提交到 API
    await submitLeadToAPI();

    // 显示感谢语
    await addDelayedMessage('system', chatTexts.value.thankYou, undefined, 600);

    _currentStep.value = 'thank_you';
    inputValue.value = '';
  }
};

// 处理跳过
const handleSkip = async () => {
  skipMessage();

  // 提交到 API
  await submitLeadToAPI();

  // 显示感谢语
  await addDelayedMessage('system', chatTexts.value.thankYou, undefined, 600);

  _currentStep.value = 'thank_you';
};

// 提交到 API
const submitLeadToAPI = async () => {
  const anonymousId = getAnonymousId();
  const sourcePage = window.location.pathname;

  // 获取服务类型的全称（去掉 emoji）
  const serviceKey = _leadData.value.service;
  const serviceFullName = serviceKey
    ? chatTexts.value.services[serviceKey]?.replace(/^[^\w\s]+\s*/, '') || serviceKey
    : '';

  const requestData = {
    data: {
      anonymous_id: anonymousId,
      service_type: serviceFullName,
      contact_method: _leadData.value.contactMethod,
      contact_value: _leadData.value.contactValue,
      message: _leadData.value.message || '',
      locale: locale.value,
      source_page: sourcePage,
    }
  };

  const apiUrl = `${API_BASE_URL}/api/chat-with-us-entries`;

  // 获取完整的提交事件参数（包含 lead_id, 双归因参数, UTM 等）
  const chatLeadParams = getChatLeadParams(
    'creston_chat',
    serviceFullName,
    _leadData.value.contactMethod
  );

  // 🔴 chat_lead: 发送请求时触发（用户提交联系方式）
  pushToDataLayer('chat_lead', {
    ...chatLeadParams,
  });
  console.log('[Chat Lead] chat_lead event fired (request sent)');

  try {
    console.log('========== ChatWithUs API Request ==========');
    console.log('URL:', apiUrl);
    console.log('Method:', 'POST');
    console.log('Headers:', { 'Content-Type': 'application/json' });
    console.log('Request Body:', JSON.stringify(requestData, null, 2));
    console.log('=============================================');

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    console.log('Response Status:', response.status);
    console.log('Response OK:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response Error Body:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Response Data:', JSON.stringify(result, null, 2));
    console.log('========== ChatWithUs API Success ==========');

    // 🔴 chat_lead_success: 接口成功后触发
    pushToDataLayer('chat_lead_success', {
      ...chatLeadParams,
    });
    console.log('[Chat Lead] chat_lead_success event fired (API success)');

  } catch (error) {
    console.error('========== ChatWithUs API Error ==========');
    console.error('Error:', error);

    // 触发 chat_error 事件
    pushToDataLayer('chat_error', {
      ...chatLeadParams,
      error_message: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

// 监听 isOpen 变化，开始欢迎流程
watch(isOpen, (newValue) => {
  if (newValue && currentStep.value === 'greeting') {
    startGreetingFlow();
  }
});

// 监听消息变化，滚动到底部
watch(displayMessages, () => {
  scrollToBottom();
}, { deep: true });
</script>

<style scoped>
/* 浮动按钮 */
.chat-floating-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(135deg, #0d0971 0%, #1a1a8e 100%);
  color: white;
  padding: 12px 20px;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 20px rgba(13, 9, 113, 0.4);
  z-index: 9999;
  transition: all 0.3s ease;
}

.chat-floating-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(13, 9, 113, 0.5);
}

.chat-icon {
  width: 24px;
  height: 24px;
}

.button-text {
  font-size: 14px;
  font-weight: 600;
}

/* 聊天弹窗 */
.chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 380px;
  max-width: calc(100vw - 40px);
  height: 600px;
  max-height: calc(100vh - 100px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 10000;
  overflow: hidden;
  /* 滚动隔离：防止滚动穿透到底层页面 */
  overscroll-behavior: contain;
}

/* 头部 */
.chat-header {
  background: linear-gradient(135deg, #0d0971 0%, #1a1a8e 100%);
  color: white;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-avatar {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-avatar svg {
  width: 20px;
  height: 20px;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.header-btn {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.header-btn:hover {
  background: rgba(255, 255, 255, 0.4);
  border-color: rgba(255, 255, 255, 0.6);
}

.btn-icon {
  color: white;
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
}

.minimize-btn .btn-icon {
  margin-top: -4px;
}

.close-btn .btn-icon {
  font-size: 22px;
}

/* 消息区域 */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
  /* 滚动隔离：防止滚动穿透到底层页面 */
  overscroll-behavior: contain;
}

/* 消息 */
.message {
  margin-bottom: 16px;
}

.message-bubble {
  max-width: 85%;
  padding: 12px 16px;
  border-radius: 16px;
  line-height: 1.5;
}

.system-bubble {
  background: #0d0971;
  color: white;
  border-bottom-left-radius: 4px;
}

.user-bubble {
  background: white;
  color: #333;
  margin-left: auto;
  border-bottom-right-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.message-content {
  font-size: 14px;
}

/* 选项按钮 */
.options-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-button {
  background: white;
  border: 2px solid #0d0971;
  color: #0d0971;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
}

.option-button:hover {
  background: #0d0971;
  color: white;
}

/* 输入区域 */
.input-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.chat-input,
.chat-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px !important; /* iOS Safari 需要 >= 16px 才不会自动放大 */
  color: #333; /* 黑色文字 */
  background: white;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  /* 防止 iOS 自动缩放 */
  -webkit-text-size-adjust: 100%;
  touch-action: manipulation;
}

.chat-input:focus,
.chat-textarea:focus {
  border-color: #0d0971;
  font-size: 16px !important; /* 确保聚焦时也是 16px */
}

.chat-textarea {
  resize: none;
}

.input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.skip-button {
  background: #f0f0f0;
  border: none;
  color: #666;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.skip-button:hover {
  background: #e0e0e0;
}

.send-button {
  background: #0d0971;
  border: none;
  color: white;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.send-button:hover {
  background: #1a1a8e;
}

.validation-error {
  color: #f44336;
  font-size: 12px;
  margin-top: 8px;
}

/* 底部 */
.chat-footer {
  padding: 12px;
  text-align: center;
  font-size: 11px;
  color: #999;
  border-top: 1px solid #e0e0e0;
  background: white;
}

/* 动画 */
.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: all 0.3s ease;
}

.chat-slide-enter-from,
.chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.message-fade-enter-active {
  transition: all 0.3s ease;
}

.message-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

/* 移动端适配 */
@media (max-width: 480px) {
  .chat-widget {
    bottom: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .chat-floating-button {
    bottom: 16px;
    right: 16px;
    padding: 10px 16px;
  }

  .button-text {
    display: none;
  }

  .chat-floating-button {
    border-radius: 50%;
    width: 56px;
    height: 56px;
    padding: 0;
    justify-content: center;
  }
}
</style>

